import { create } from 'zustand';
import apiClient from '../service/apiClient';
import { Page, ResponseError } from '../type/type';
import { toast } from 'react-toastify';
import { isAxiosError } from 'axios';

export type PurchaseLandingRequestStatus =
  | 'PENDING_PAYMENT'
  | 'ORDER_CREATED';

export interface IPurchaseLandingCustInfo {
  id: number;
  sequence: number;
  name: string;
  solarType: string;
  year: number;
  month: number;
  day: number;
  hour?: number;
  minute?: number;
  sexType: string;
  birthday: string;
}

export interface IPurchaseLandingRequest {
  id: number;
  trackingCodeId: number;
  campaignName: string;
  trkId: string;
  companyName?: string;
  landingDeleted?: boolean; // 신청의 랜딩이 삭제된 경우 true
  packageId: number;
  // 패키지/가격은 신청 시점의 스냅샷이 우선. 과거 데이터엔 백엔드에서 마스터값으로 fallback되거나
  // 양쪽 모두 부재해 null/0이 들어올 수 있음 → 화면에서 null 가드 필요.
  packageName?: string;
  packagePrice?: number;
  productNames?: string[];
  status: PurchaseLandingRequestStatus;
  name: string;
  phone: string;
  custInfos: IPurchaseLandingCustInfo[];
  paymentConfirmedAt?: string;
  orderCreatedAt?: string;
  consultOrderId?: string;
  unpaidAlimtalk1SentAt?: string;
  unpaidAlimtalk2SentAt?: string;
  paidAlimtalkSentAt?: string;
  createdAt: string;
}

export interface IPurchaseLandingRequestSearch {
  status?: PurchaseLandingRequestStatus;
  name?: string;
  phone?: string;
  franchiseIds?: string[];
  startDt?: string;
  endDt?: string;
}

export interface IPurchaseLandingStatistics {
  pendingPaymentCount: number;
  pendingPaymentAmount: number;
  orderCreatedCount: number;
  orderCreatedAmount: number;
  totalCount: number;
  totalAmount: number;
}

interface PurchaseLandingRequestResponse extends Page {
  content: IPurchaseLandingRequest[];
}

type PurchaseLandingRequestState = {
  requestList: IPurchaseLandingRequest[];
  requestPage: Page | null;
  statistics: IPurchaseLandingStatistics | null;
  loading: boolean;
  search: IPurchaseLandingRequestSearch;
};

type PurchaseLandingRequestAction = {
  setSearch: (search: IPurchaseLandingRequestSearch) => void;
  fetchRequests: (page?: number, size?: number) => Promise<void>;
  fetchStatistics: () => Promise<void>;
  confirmPayment: (id: number) => Promise<boolean>;
  sendAlimtalk: (id: number, type: 'UNPAID1' | 'UNPAID2' | 'PAID') => Promise<boolean>;
  deleteRequest: (id: number) => Promise<boolean>;
  reset: () => void;
};

export const usePurchaseLandingRequestStore = create<
  PurchaseLandingRequestState & PurchaseLandingRequestAction
>((set, get) => ({
  requestList: [],
  requestPage: null,
  statistics: null,
  loading: false,
  search: {},

  setSearch: (search: IPurchaseLandingRequestSearch) => {
    set({ search });
  },

  fetchRequests: async (page = 0, size = 20) => {
    set({ loading: true });
    try {
      const { search } = get();
      const response = await apiClient.get<PurchaseLandingRequestResponse>(
        '/purchase-landing/request',
        {
          params: {
            ...search,
            franchiseIds: search.franchiseIds?.length ? search.franchiseIds.join(',') : undefined,
            page,
            size,
            sort: 'createdAt,desc',
          },
        }
      );
      const { content, ...rest } = response.data;
      set({ requestList: content, requestPage: rest });
    } catch (error) {
      console.error(error);
      toast.error('신청 내역을 불러오지 못했습니다.');
    }
    set({ loading: false });
  },

  fetchStatistics: async () => {
    try {
      const { search } = get();
      const response = await apiClient.get<IPurchaseLandingStatistics>(
        '/purchase-landing/request/statistics',
        {
          params: {
            ...search,
            franchiseIds: search.franchiseIds?.length ? search.franchiseIds.join(',') : undefined,
          },
        }
      );
      set({ statistics: response.data });
    } catch (error) {
      console.error(error);
    }
  },

  confirmPayment: async (id: number) => {
    try {
      await apiClient.patch(`/purchase-landing/request/${id}/payment-confirm`);
      toast.success('입금 확인 처리되었습니다.');
      return true;
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg ?? '입금 확인에 실패했습니다.');
      } else {
        console.error(error);
        toast.error('입금 확인에 실패했습니다.');
      }
      return false;
    }
  },

  sendAlimtalk: async (id: number, type: 'UNPAID1' | 'UNPAID2' | 'PAID') => {
    try {
      await apiClient.post(`/purchase-landing/request/${id}/send-alimtalk`, { type });
      const labels: Record<string, string> = { UNPAID1: '미입금', UNPAID2: '미입금 재발송', PAID: '입금완료' };
      toast.success(`${labels[type]} 알림톡이 발송되었습니다.`);
      return true;
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg ?? '알림톡 발송에 실패했습니다.');
      } else {
        console.error(error);
        toast.error('알림톡 발송에 실패했습니다.');
      }
      return false;
    }
  },

  deleteRequest: async (id: number) => {
    try {
      await apiClient.delete(`/purchase-landing/request/${id}`);
      toast.success('신청이 삭제되었습니다.');
      return true;
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg ?? '삭제에 실패했습니다.');
      } else {
        console.error(error);
        toast.error('삭제에 실패했습니다.');
      }
      return false;
    }
  },

  reset: () => {
    set({ requestList: [], requestPage: null, loading: false, search: {} });
  },
}));
